export type UserRole = "cliente" | "affiliato" | "operatore" | "operatore_supervisore" | "admin" | "responsabile";

export interface User {
  id: string;
  email: string;
  name: string;
  roles: UserRole[];
  contactId: string | null;
  espoUserId: string | null;
  agencyName: string | null;
  /** Chiave tour → versione vista. I valori scritti prima del 2026-09-12 sono `true` = versione 1. */
  onboarding_flags: Record<string, number | boolean>;
  /** Voci del menù accese per l'agenzia dello staff; null/assente = menù di sempre. */
  vociMenu?: Record<VoceMenu, boolean> | null;
}

export type VoceMenu = "dashboard" | "preventivo" | "consulenza" | "pratiche" | "clienti" | "telegram";

export interface Bolletta {
  id: string;
  fornitore: string;
  tipo: "Luce" | "Gas";
  importo: number;
  consumo: number;
  unitaConsumo: "kWh" | "Smc";
  data: string;
  stato: "Caricata" | "Analizzata" | "Errore";
}

export interface Offerta {
  id: string;
  fornitore: string;
  nome: string;
  tipo: "Luce" | "Gas" | "Dual";
  prezzoUnitario: number;
  unitaPrezzo: "€/kWh" | "€/Smc";
  quotaFissaMese: number;
  durataMesi: number;
  attiva: boolean;
}

// Relax PraticaStage from literal to string (backend validates, 7 real stages are STAGES const)
export type PraticaStage = string;

// Compact bolletta shown in PraticaDetail "Bollette collegate"
export interface BollettaCollegata {
  id: string;
  name: string;
  fornitoreAttuale: string;
  tipoFornitura: string;
  dataBolletta: string | null;
  importoMensile: number;
  hasFile: boolean;
}

export interface Pratica {
  id: string;
  nome: string;
  offerta: string;
  tipo: "Luce" | "Gas" | "Dual";
  stage: PraticaStage;
  dataCreazione: string;
  fornitoreAttuale: string;
  fornitoreNuovo: string;
  prezzoUnitario: number;
  unitaPrezzo: "€/kWh" | "€/Smc";
  quotaFissaMese: number;
  durataMesi: number;
  risparmioAnnuo: number;
  documenti: Documento[];
  clienteNome?: string;
  agenzia?: string;
  commissione?: number;
  bollette?: BollettaCollegata[];  // only in detail endpoint
}

// Shape returned by GET /api/espo/bollette (new in 2c)
export interface BollettaApi {
  id: string;
  name: string;
  fornitoreAttuale: string;
  tipoFornitura: string;
  dataBolletta: string | null;
  kwhAnnui: number;
  smcAnnui: number;
  consumoStimato?: boolean;
  importoMensile: number;
  codicePod: string | null;
  codicePdr: string | null;
  hasFile: boolean;
  fileName: string | null;
}

export interface BollettaApiDetail extends BollettaApi {
  indirizzoFornitura: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    interno?: string;
  };
  note: string;
}

export interface BolletteListResponse {
  total: number;
  items: BollettaApi[];
}

export interface Documento {
  id: string;
  nome: string;
  tipo: string;
}

export interface Commissione {
  mese: string;
  contratti: number;
  commissioni: number;
  bonus: number;
  totale: number;
  statoPagamento: "Pagato" | "In maturazione" | "Da pagare";
}

export interface AccordoCommissione {
  tipo: string;
  valore: string;
  note: string;
}

export interface Agenzia {
  id: string;
  nome: string;
  tipoCommissione: string;
  valoreCommissione: string;
  contrattiMese: number;
  commissioniMese: number;
  note: string;
}

export interface ClienteAgenzia {
  id: string;
  nome: string;
  email: string;
  telefono: string;
  pratiche: number;
  praticheAttive: number;
  ultimoContatto: string;
}

export interface AttivitaRecente {
  id: string;
  clienteNome: string;
  azione: string;
  tempo: string;
}

export interface Notifica {
  id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  attachment?: { name: string; type: string };
}

// === Affiliato (fase 2d) ===

export interface ContrattoAffiliato {
  id: string;
  name: string;
  stage: string;
  close_date: string | null;
  created_at: string | null;
  amount: number | null;
  cliente_name: string | null;
  cliente_id: string | null;
  tipo_soggetto?: string | null;
  tipo?: string | null;
  quota_agenzia: number | null;
  readiness?: Readiness | null;
}

export interface ClienteAffiliato {
  id: string;
  first_name: string | null;
  last_name: string | null;
  nome_visualizzato?: string | null;
  email: string | null;
  phone: string | null;
  opportunities_count: number;
}

export interface CommissioneRow {
  opportunity_id: string;
  opportunity_name: string;
  cliente_name: string | null;
  close_date: string | null;
  quota_agenzia: number;
  operatore_id: string | null;
  operatore_name: string | null;
}

export interface SeriePunto { month: string; total: number; count: number }
export interface SerieResponse { punti: SeriePunto[] }
export interface OperatoreRiga {
  operatore_id: string | null;
  operatore_name: string | null;
  agenzia_name: string | null;
  num_pratiche: number;
  totale: number;
}
export interface OperatoriResponse { rows: OperatoreRiga[]; totale: number }
export interface PipelineResponse { maturato: number; pratiche_pipeline: number }

export interface DashboardAffiliatoResponse {
  month: string;
  contratti_mese_count: number;
  contratti_mese_precedente_count: number;
  commissioni_mese_total: number;
  contratti_totali_count: number;
  clienti_count: number;
  ultimi_contratti: ContrattoAffiliato[];
}

export interface ContrattiListResponse {
  total: number;
  items: ContrattoAffiliato[];
  // false = auto-invio A2A spento (globale o per l'agenzia): le pratiche pronte
  // NON partono da sole, vanno inviate a mano.
  invio_automatico_attivo?: boolean;
  // Utenza fornitore con cui l'agenzia lavora le pratiche (null = nessuna assegnata).
  utenza_fornitore?: UtenzaFornitoreDTO | null;
  // true = la lettura dell'utenza è fallita: `utenza_fornitore` è null per un guasto,
  // non perché manchi l'assegnazione.
  utenza_fornitore_non_disponibile?: boolean;
}

export interface CommissioniResponse {
  year: number;
  month: number;
  rows: CommissioneRow[];
  total: number;
}

export interface ClientiListResponse {
  total: number;
  items: ClienteAffiliato[];
}

export interface OffertaScelta {
  nome: string;
  fornitore: string;
  tipo: string;
  prezzo: string;
  quota_fissa_mese: number;
  durata_mesi: number;
  fatturazione: string;
}

// === Agenzia condivise (fase 2e) ===

// Readiness invio automatico A2A (Task 8bis backend → Task 9 frontend)
export interface Readiness {
  pronta: boolean;
  manca: string[];
  applicabile: boolean;
  // Perché è fuori dall'auto-invio, in codice: la pagina ne fa cose diverse.
  // "strada_vecchia" = non parte dal portale, si inserisce a mano su A2A (mirror di
  // backend/app/a2a_autoinvia/predicate.py).
  motivo?: "gia_inviata" | "stato_non_da_inviare" | "offerta_non_a2a" | "tipo_non_supportato"
    | "strada_vecchia" | null;
}

export interface DashboardAgenziaResponse {
  pratiche_totali: number;
  clienti_totali: number;
  da_lavorare: number;
  stage_counts: Record<string, number>;
  ultime_pratiche: ContrattoAffiliato[];
}

export type ModuloTipo = "titolo_occupazione" | "regolarita_edilizia";

export const LABEL_MODULO: Record<ModuloTipo, string> = {
  titolo_occupazione: "Titolo occupazione",
  regolarita_edilizia: "Regolarità edilizia",
};

export interface DettaglioPraticaAgenzia {
  id: string;
  name: string;
  stage: string;
  close_date: string | null;
  created_at: string | null;
  amount: number | null;
  cliente_name: string | null;
  cliente_id: string | null;
  tipo_soggetto?: string | null;
  quota_agenzia: number | null;
  tipo: string | null;
  pod: string | null;
  pdr: string | null;
  codice_fiscale: string | null;
  iban: string | null;
  description: string | null;
  account_name: string | null;
  a2a_numero_pratica?: string | null;   // numero pratica presso A2A (OP…), quando esiste
  bollette?: { id: string; name: string; fornitoreAttuale: string; tipoFornitura: string; dataBolletta: string | null; importoMensile: number; hasFile: boolean }[];
  has_documento?: boolean;
  documento_abilitato?: boolean;
  offerta_scelta?: OffertaScelta | null;
  tipo_pratica?: string | null;
  moduli_firmati?: { id: string; name: string; tipo: ModuloTipo | null }[];
  moduli_stato?: {
    richiesti: ModuloTipo[];
    presenti: ModuloTipo[];
    mancanti: ModuloTipo[];
    completo: boolean;
  } | null;
  readiness?: Readiness | null;
  invio_automatico_attivo?: boolean;
  // Utenza fornitore con cui questa pratica verrà inviata (null = nessuna assegnata).
  utenza_fornitore?: UtenzaFornitoreDTO | null;
  utenza_fornitore_non_disponibile?: boolean;
}

export interface DettaglioClienteAgenzia {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  codice_fiscale: string | null;
  iban: string | null;
  indirizzo: string | null;
  opportunities_count: number;
  pratiche: ContrattoAffiliato[];
  has_documento?: boolean;
}

// === Utenze fornitore per agenzia (multiutente A2A) ===

export type Fornitore = "A2A" | "Iren" | "Sentra";
export type ModalitaUtenza = "custodite" | "sessione_temporanea";
// `da_configurare`: riga creata dall'admin, credenziali ancora da inserire dall'agenzia.
export type StatoUtenza = "attiva" | "sessione_scaduta" | "da_ripristinare" | "da_configurare";

export interface UtenzaFornitoreInfo {
  fornitore: Fornitore;
  disponibile: boolean;
  // l'admin ha assegnato un'utenza a questa agenzia: senza, le pratiche restano in coda
  assegnata: boolean;
  username: string | null;
  modalita: ModalitaUtenza | null;
  stato: StatoUtenza | null;
  motivo_stato: string | null;
  stato_at: string | null;
  ultimo_accesso_ok_at: string | null;
  predefinita: boolean;
  mia: boolean;
  condivisa_con: number;
  modalita_ammesse: ModalitaUtenza[];
}

export interface ProvaAccessoStato {
  job_id: string;
  stato: "in_corso" | "ok" | "rifiutato" | "transitorio";
  dettaglio: string;
  utenza_id?: number;
}

export interface UtenzaFornitoreDTO {
  fornitore: Fornitore;
  username: string;
  modalita: ModalitaUtenza;
  stato: StatoUtenza;
  predefinita: boolean;
}
